import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Tooltip} from '@/components/Tooltip';
import {Typography} from '@/components/Typography';
import {useGA4WithParams} from '@/hooks/tracking';
import {useMapDataSource} from '@/map/api';
import {Flexbox, SimpleListBox} from '@/styled-components';
import {palette} from '@/styles';
import {
  GrayCenterBox,
  SectionBlock,
  SectionSubTitle,
  SectionTitle,
} from '../../StyledComponents';
import {GenderAgeBarChart} from '../Chart/GenderAgeBarChart';
import {EMPTY_ITEM_TYPES, OFFICE_WORKER_AGES} from '../constants';
import {EmptyItem} from '../EmptyItem';
import {PopulationDataProps} from '../interface';
import {addGenderAgeTotalValue} from '../utils';

export const OfficeWorkerCompare = ({
  isPending,
  error,
  data: rawdata,
}: PopulationDataProps) => {
  const {data: dataSource} = useMapDataSource();
  const metadata = useMemo(
    () => dataSource?.find((source) => source.key === 'officeWorker'),
    [dataSource]
  );

  const data = useMemo(() => {
    return addGenderAgeTotalValue(
      rawdata?.map((i) => i.officeWorker),
      OFFICE_WORKER_AGES.length
    );
  }, [rawdata]);

  return (
    <Root>
      <div>
        <Flexbox>
          <SectionTitle>직장 인구</SectionTitle>
          <Tooltip
            openOnClick
            closeEvents={{
              mouseout: false,
              blur: false,
              click: true,
            }}
            iconStyle={{marginLeft: 2}}
            render={() => (
              <SimpleListBox
                style={{color: palette.gray200, lineHeight: '16px'}}
              >
                <ul>
                  <li>SKT에서 전국 사업체 조사 기반으로 추정한 정보입니다.</li>
                  <li>
                    소수점 처리로 인해 보고서 및 분석 결과의 총 직장 인구수와
                    미세한 차이가 있을 수 있습니다.
                  </li>
                </ul>
              </SimpleListBox>
            )}
          />
        </Flexbox>
        <SectionSubTitle>{`${metadata?.source || '마이프차'}∙${
          metadata?.baseDate || ''
        }∙명`}</SectionSubTitle>
      </div>
      {isPending ? (
        <GrayCenterBox>
          <LoadingBar color={palette.textSecondary} />
        </GrayCenterBox>
      ) : error ? (
        <Typography variant="medium12" color={palette.textSecondary}>
          분석 실패
        </Typography>
      ) : !data ? (
        <EmptyItem type={EMPTY_ITEM_TYPES.Population} />
      ) : (
        <GenderAreaBarChartBox items={data} />
      )}
    </Root>
  );
};

const GenderAreaBarChartBox = ({items}: {items: any[]}) => {
  const tracking = useGA4WithParams();
  const [tab, setTab] = useState<number>(0);
  const compareList = items.map((_, idx) => `비교 상권 ${idx + 1}`);

  const onTabChange = (ageIndex: number) => {
    tracking('analysis_compare_companyperson', {
      age_info: OFFICE_WORKER_AGES[ageIndex],
    });
    setTab(ageIndex);
  };

  return (
    <GenderAgeBarChart
      type="직장 인구"
      data={items}
      compareList={compareList}
      ageIndex={tab}
      ages={OFFICE_WORKER_AGES}
      onAgeChange={onTabChange}
    />
  );
};

const Root = styled(SectionBlock)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
